import { NextPage, GetStaticProps } from "next";
import { RichText } from "../../components/richText";
import {
  PrivacyPolicyData,
  getPrivacyPolicyQuery,
} from "../../queries/privacyPolicyQuery";
import { PageProps } from "../../types/pageProps";
import { Previewable } from "../../types/previewable";
import { getStaticPageData } from "../../utils/getStaticPageData";
import { QueryParams } from "next-sanity";
import LiveQuery from "@sanity/preview-kit/live-query";
import Head from "next/head";
import {
  HeaderNavigationProps,
  HeaderNavigationQuery,
} from "@/components/HeaderNavigation";
import { getClient } from "@/lib/sanity.server";
import HeaderNavigation from "@/components/HeaderNavigation/HeaderNavigation";
import FooterModular from "@/components/FooterModular";
import {
  FooterNavigationProps,
  FooterNavigationQuery,
} from "@/components/FooterNavigation/query";

export type PrivacyPolicyProps = Previewable<PageProps<PrivacyPolicyData>>;

const token = process.env.SANITY_API_TOKEN;

const PrivacyPolicyLanding: NextPage<PrivacyPolicyProps> = (
  props: PrivacyPolicyProps,
) => {
  const privacyLandingData = props.payload.data;

  if (privacyLandingData === null) {
    return null;
  }

  const { heading, subHeading, bodyRichText }: PrivacyPolicyData =
    privacyLandingData;

  return (
    <LiveQuery
      enabled={props.preview}
      query={getPrivacyPolicyQuery(props.preview)}
      initialData={props.payload.data}
    >
      <>
        <Head>
          <title>{`Privacy Policy • PairTree`}</title>
        </Head>
        <HeaderNavigation navSections={props.payload.data.navSections} />
        <div className="text-body-2 mt-20 flex w-full flex-col items-center">
          <div className="flex max-w-prose flex-col gap-2">
            <h1 className="text-head-2 mb-8 mt-6 text-center">{heading}</h1>

            <p> {subHeading} </p>

            <RichText content={bodyRichText}></RichText>
          </div>
        </div>
        <FooterModular footerData={props.payload.data.footerPayload} />
      </>
    </LiveQuery>
  );
};

export const getStaticProps: GetStaticProps<PrivacyPolicyProps> = async (
  context,
) => {
  const preview: boolean = !!context.draftMode;
  const queryParams: QueryParams = {};
  const query = getPrivacyPolicyQuery(preview);

  const privacyPayload = await getStaticPageData<PrivacyPolicyData>({
    query,
    queryParams,
    preview,
  });

  if (
    privacyPayload === null ||
    !privacyPayload.data.pageFeatureFlag?.enabled
  ) {
    return {
      notFound: true,
    };
  }

  const navPayload: HeaderNavigationProps = await getClient(preview).fetch(
    HeaderNavigationQuery.query,
    {},
  );

  const footerPayload: FooterNavigationProps = await getClient(preview).fetch(
    FooterNavigationQuery.query,
    {},
  );

  const payload = {
    ...privacyPayload,
    data: {
      ...privacyPayload.data,
      ...navPayload,
      footerPayload: footerPayload,
    },
  };

  return {
    props: {
      payload: payload,
      preview,
      token: preview ? token : "",
    },
  };
};

export default PrivacyPolicyLanding;
